import { Box, Button, IconButton, Typography, Divider, CircularProgress, TextField, Link } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import { useState, useContext, useEffect } from "react";
import { WidgetContext } from "src/contexts/WidgetContext";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { addCredits } from "src/api/addCredits";
import { applyPromoCode } from "src/api/applyPromoCode";
import { selectFreePlan } from "src/api/selectFreePlan";
import SubsPlans from "./SubsPlans";
import BoosterPacks from "./BoosterPacks";
import TablePricing from "./TablePricing";
import PaymentDialog from "./PaymentDialog";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import { createCheckoutSession } from "src/api/createCheckoutSession";
import { getProducts, isAndroidNative, isIosNative, purchaseProduct } from "src/services/iap";
import { verifyApplePayment } from "src/api/verifyApplePayment";
import { verifyGooglePayment } from "src/api/verifyGooglePayment";
import {
  BOOSTER_POWER_PRICE_USD,
  BOOSTER_STARTER_PRICE_USD,
  SUB_ANNUAL_PRO_PRICE_USD,
  SUB_BASIC_MONTHLY_PRICE_USD,
  SUB_MONTHLY_PRICE_USD,
} from "src/config/products.config";
import { frontEnvNumber, subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import { getSeasonPassPrice } from "src/config/products.config";
import { getPrivacyPolicyUrl, getTermsConditionsUrl } from "src/utils/legalPageUrls";
import { isDevFrontendEnv } from "src/config/frontendUrls";
import { useAuth0 } from "@auth0/auth0-react";
import { getProductConfig } from "src/config/products.config";
import { planDurationFromProductId, trackBeginCheckout, trackPurchase, trackSubscriptionFailed } from "src/utils/analyticsCapFirebase";

const DEV_PAYMENT_BLOCK_TOOLTIP = "Payments are disabled in dev. Use prod to complete purchases.";

const isDevOrLocalPaymentsEnvironment = () => isDevFrontendEnv();


function seasonPassTierCredits(tier: string): number {
  if (tier === "2") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_2_CREDITS", 1600);
  if (tier === "3") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_3_CREDITS", 800);
  return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_1_CREDITS", 2400);
}

function seasonPassTierMonths(tier: string): number {
  if (tier === "2") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_2_DURATION_MONTHS", 4);
  if (tier === "3") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_3_DURATION_MONTHS", 2);
  return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_1_DURATION_MONTHS", 6);
}

// Helper: season pass price-per-credit by tier (runtime Mongo → env → defaults)
const getSeasonPassCredits = (seasonPassPrice: string): string => {
  const tier = seasonPassPrice === "2" || seasonPassPrice === "3" ? seasonPassPrice : "1";
  return getPricePerCredit(getSeasonPassPrice(tier), seasonPassTierCredits(tier));
};

const getSeasonPassPriceDisplay = (seasonPassPrice: string): string => {
  const tier = seasonPassPrice === "2" || seasonPassPrice === "3" ? seasonPassPrice : "1";
  const suffix = tier === "1" ? "per season" : "rest of season";
  return `$${getSeasonPassPrice(tier).toFixed(2)} / ${suffix}`;
};

// Helper: format price per credit from price and credits (from env or config)
// unit "EA" for boosters (e.g. "$0.07/EA"), default "CREDIT" (e.g. "$0.02 / CREDIT")
function getPricePerCredit(priceDollars: number, credits: number, unit: "CREDIT" | "EA" = "CREDIT"): string {
  if (credits <= 0) return unit === "EA" ? "$ -/EA" : "$ - / CREDIT";
  const perCredit = priceDollars / credits;
  // Truncate to 3 decimal places (do not round)
  // const truncated = Math.floor(perCredit * 1000) / 1000;
  // const formatted = truncated.toFixed(3);
  const formatted =
    perCredit >= 0.01 ? perCredit.toFixed(3) : perCredit >= 0.001 ? perCredit.toFixed(3) : `~${perCredit.toFixed(3)}`;
  if (unit === "EA") return `$${formatted}/EA`;
  return `$${formatted} / CREDIT`;
}

// Function to get monthly plans with dynamic season pass price
const getMonthlyPlans = (seasonPassPrice: string) => {
  const basicMonthlyCredits = frontEnvNumber("REACT_APP_SUB_BASIC_MONTHLY_CREDITS", 100);
  const basicMonthlyPriceUsd = frontEnvNumber("REACT_APP_SUB_BASIC_MONTHLY_PRICE", SUB_BASIC_MONTHLY_PRICE_USD);
  const monthlyCredits = frontEnvNumber("REACT_APP_SUB_MONTHLY_CREDITS", 400);
  const monthlyPriceUsd = frontEnvNumber("REACT_APP_SUB_MONTHLY_PRICE", SUB_MONTHLY_PRICE_USD);
  const annualCredits = frontEnvNumber("REACT_APP_SUB_ANNUAL_PRO_CREDITS", 12000);
  const annualPriceUsd = frontEnvNumber("REACT_APP_SUB_ANNUAL_PRO_PRICE", SUB_ANNUAL_PRO_PRICE_USD);
  return [
    // {
    //   name: "Standard",
    //   credits: "40 Credits / mo",
    //   creditsAmount: 40,
    //   subscription: "standard",
    //   productId: "league_connect_standard_monthly",
    //   price: "$9.99 / month",
    //   features: [
    //     { text: "No Ads", icon: "check" },
    //     { text: "Multi-step Reasoning", icon: "check" },
    //   ],
    //   isPremium: false,
    // },
    // {
    //   name: "Premium",
    //   credits: "100 Credits / mo",
    //   creditsAmount: 100,
    //   subscription: "premium",
    //   productId: "league_connect_premium_monthly",
    //   price: "$19.99 / month",
    //   features: [
    //     { text: "Live Audio Chat", icon: "star" },
    //     { text: "Draft Kit", icon: "star" },
    //     { text: "30 Day Credit Rollover", icon: "star" },
    //   ],
    //   isPremium: true,
    // },
    // New plans from UI design
    {
      name: "FREE",
      credits: "AD SUPPORTED",
      creditsAmount: 0,
      subscription: "free",
      productId: "league_connect_free",
      price: "$0.00",
      features: [
        { text: "Save league logins/details", icon: "check" },
        { text: "Customized League Rankings", icon: "check" },
        { text: "Customized News", icon: "check" },
        { text: "League Specific Trade Values", icon: "check" },
        { text: "Articles", icon: "check" },
        // { text: "Advanced Reasoning", icon: "cross" },
      ],
      isPremium: false,
      isFree: true,
      adSupported: true,
    },
    {
      name: "BASIC MONTHLY",
      credits: getPricePerCredit(basicMonthlyPriceUsd, basicMonthlyCredits),
      creditsAmount: basicMonthlyCredits,
      subscription: "basic_monthly",
      productId: "league_connect_basic_monthly",
      price: `$${basicMonthlyPriceUsd.toFixed(2)} / per month`,
      features: [
        { text: `${basicMonthlyCredits} Credits Per Month`, icon: "check" },
        { text: "Advanced Reasoning", icon: "check" },
      ],
      isPremium: false,
    },
    {
      name: "ROTOGPT MONTHLY",
      credits: getPricePerCredit(monthlyPriceUsd, monthlyCredits),
      creditsAmount: monthlyCredits,
      subscription: "monthly",
      productId: "league_connect_monthly",
      price: `$${monthlyPriceUsd.toFixed(2)} / per month`,
      features: [
        { text: `${monthlyCredits} Credits Per Month`, icon: "check" },
        { text: "Advanced Reasoning", icon: "check" },
      ],
      isPremium: false,
    },
    //COMMENTED OUT FOR NOW THE SEASON PASS (ALSO IN MANAGEMEMBERSHIP.TSX and TABLEPRICING.TSX) BECAUSE ITS WORKING IN STRIPE, NOT IN APPLE/GOOGLE PAY
    // {
    //   name: "SEASON PASS",
    //   credits: getSeasonPassCredits(seasonPassPrice),
    //   creditsAmount: SEASON_PASS_TIER_CREDITS[seasonPassPrice] || SEASON_PASS_TIER_CREDITS["1"],
    //   subscription: "season_pass",
    //   productId: "league_connect_season_pass",
    //   price: getSeasonPassPriceDisplay(seasonPassPrice),
    //   features: [
    //     { text: `${(SEASON_PASS_TIER_CREDITS[seasonPassPrice] || SEASON_PASS_TIER_CREDITS["1"]).toLocaleString()} Total Credits (${Math.floor((SEASON_PASS_TIER_CREDITS[seasonPassPrice] || SEASON_PASS_TIER_CREDITS["1"]) / (SEASON_PASS_TIER_MONTHS[seasonPassPrice] || SEASON_PASS_TIER_MONTHS["1"]))} per month)`, icon: "check" },
    //     { text: "Access Aug - Feb", icon: "check" },
    //     { text: "Great for Redraft", icon: "check" },
    //   ],
    //   isPremium: false,
    //   isMostPopular: true,
    //   autoRenews: "AUTO-RENEWS AUG 1ST",
    //   isDisabled: seasonPassPrice === "4",
    // },
    {
      name: "ROTOGPT ANNUAL",
      credits: getPricePerCredit(annualPriceUsd, annualCredits),
      creditsAmount: annualCredits,
      subscription: "annual_pro",
      productId: "league_connect_annual_pro",
      price: `$${annualPriceUsd.toFixed(2)} / per year`,
      features: [
        { text: `${Math.floor(annualCredits / 12).toLocaleString()} Credits Per Month`, icon: "check" },
        { text: "Full Dynasty Support", icon: "check" },
        { text: "365-Day Access", icon: "check" },
      ],
      isPremium: true,
      isBestValue: true,
      autoRenews: "RENEWS ON ANNIVERSARY",
    },
  ];
};

function buildBoosterPacks() {
  const starterCredits = frontEnvNumber("REACT_APP_BOOSTER_STARTER_CREDITS", 30);
  const starterPriceUsd = frontEnvNumber("REACT_APP_BOOSTER_STARTER_PRICE", BOOSTER_STARTER_PRICE_USD);
  const powerCredits = frontEnvNumber("REACT_APP_BOOSTER_POWER_CREDITS", 250);
  const powerPriceUsd = frontEnvNumber("REACT_APP_BOOSTER_POWER_PRICE", BOOSTER_POWER_PRICE_USD);
  return [
    // {
    //   credits: "5 Credits",
    //   price: "$2.99",
    //   creditsNumber: 5,
    //   subscription: "booster_5",
    //   productId: "league_connect_boost_5",
    // },
    // {
    //   credits: "25 Credits",
    //   price: "$9.99",
    //   creditsNumber: 25,
    //   subscription: "booster_25",
    //   productId: "league_connect_boost_25",
    // },
    // New boosters from UI design
    {
      name: `STARTER BOOSTER`,
      credits: `${starterCredits} CREDITS (${getPricePerCredit(starterPriceUsd, starterCredits, "EA")})`,
      price: `$${starterPriceUsd.toFixed(2)}`,
      creditsNumber: starterCredits,
      subscription: "booster_starter",
      productId: "league_connect_boost_starter",
      pricePerCredit: getPricePerCredit(starterPriceUsd, starterCredits, "EA"),
      buttonText: "BUY NOW",
    },
    {
      name: `POWER BOOSTER`,
      credits: `${powerCredits} CREDITS (${getPricePerCredit(powerPriceUsd, powerCredits, "EA")})`,
      price: `$${powerPriceUsd.toFixed(2)}`,
      creditsNumber: powerCredits,
      subscription: "booster_power",
      productId: "league_connect_boost_power",
      pricePerCredit: getPricePerCredit(powerPriceUsd, powerCredits, "EA"),
      buttonText: "BUY NOW",
    },
  ];
}

export const ManageMembership = ({
  onClose,
  onBack,
  isRequired = false,
  onOptionSelected,
  onOpenAddPaymentMethod,
  promoCode: parentPromoCode = "",
  promoCodeId: parentPromoCodeId = null,
  promoDiscount: parentPromoDiscount = null,
  promoPercentOff: parentPromoPercentOff = null,
  promoAmountOff: parentPromoAmountOff = null,
  onPromoCodeChange,
  onPromoCodeIdChange,
  onPromoDiscountChange,
  onPromoPercentOffChange,
  onPromoAmountOffChange,
  userSubscription,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSeasonTab, setSelectedSeasonTab] = useState(0); // 0 = PRE SEASON, 1 = MID SEASON, 2 = PLAYOFF, 3 = OFF SEASON
  const [hoveredMonthlyPlan, setHoveredMonthlyPlan] = useState<number | null>(null);
  const [hoveredBoosterPack, setHoveredBoosterPack] = useState<number | null>(null);
  // Track which specific option is being processed (null = none, 'plan-0' = first plan, 'pack-0' = first pack, etc.)
  const [processingOption, setProcessingOption] = useState<string | null>(null);
  // setCredits function to store the new credits
  const { setCredits, seasonPassPrice } = useContext(WidgetContext);
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const { user } = useAuth0();
  const analyticsAuthId = user?.sub || "unknown";

  const [, setFrontRuntimeBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setFrontRuntimeBump((n) => n + 1)), []);

  // Use parent state for promo code persistence, fallback to local state if parent doesn't provide setters
  const [promoCode, setPromoCode] = useState(parentPromoCode);
  const [promoCodeId, setPromoCodeId] = useState<string | null>(parentPromoCodeId);
  const [promoDiscount, setPromoDiscount] = useState<string | null>(parentPromoDiscount);
  const [promoPercentOff, setPromoPercentOff] = useState<number | null>(parentPromoPercentOff);
  const [promoAmountOff, setPromoAmountOff] = useState<number | null>(parentPromoAmountOff);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  // Payment dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDialogSuccess, setPaymentDialogSuccess] = useState(false);
  const [paymentDialogMessage, setPaymentDialogMessage] = useState<string | undefined>(undefined);
  const paymentsDisabledInCurrentEnv = isDevOrLocalPaymentsEnvironment();
  // const paymentsDisabledInCurrentEnv = false; // TEMP: enable dev payments test

  // Sync with parent state when props change
  useEffect(() => {
    setPromoCode(parentPromoCode);
    setPromoCodeId(parentPromoCodeId);
    setPromoDiscount(parentPromoDiscount);
    setPromoPercentOff(parentPromoPercentOff);
    setPromoAmountOff(parentPromoAmountOff);
  }, [parentPromoCode, parentPromoCodeId, parentPromoDiscount, parentPromoPercentOff, parentPromoAmountOff]);

  // Handle promo code application
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setIsApplyingPromo(true);
    setPromoError(null);

    try {
      const accessToken = await getSafeAccessAuth0Token();
      if (!accessToken) {
        setPromoError("Authentication error. Please log in again.");
        setIsApplyingPromo(false);
        return;
      }

      const result = await applyPromoCode(accessToken, promoCode.trim().toUpperCase(), "stripe");

      if (result.success) {
        const newPromoCodeId = result.promoCodeId || null;
        const newPromoDiscount = result.discountDescription || null;
        const newPromoPercentOff = result.percentOff || null;
        const newPromoAmountOff = result.amountOff || null;

        // Update local state
        setPromoCodeId(newPromoCodeId);
        setPromoDiscount(newPromoDiscount);
        setPromoPercentOff(newPromoPercentOff);
        setPromoAmountOff(newPromoAmountOff);
        setPromoError(null);

        // Update parent state for persistence
        onPromoCodeIdChange?.(newPromoCodeId);
        onPromoDiscountChange?.(newPromoDiscount);
        onPromoPercentOffChange?.(newPromoPercentOff);
        onPromoAmountOffChange?.(newPromoAmountOff);

        console.log("✅ Promo code applied:", result.discountDescription);
      } else {
        // Clear local state
        setPromoCodeId(null);
        setPromoDiscount(null);
        setPromoPercentOff(null);
        setPromoAmountOff(null);
        setPromoError(result.message || "Invalid promo code");

        // Clear parent state
        onPromoCodeIdChange?.(null);
        onPromoDiscountChange?.(null);
        onPromoPercentOffChange?.(null);
        onPromoAmountOffChange?.(null);
      }
    } catch (error) {
      console.error("Error applying promo code:", error);
      setPromoError("Failed to apply promo code. Please try again.");

      // Clear local state
      setPromoCodeId(null);
      setPromoDiscount(null);
      setPromoPercentOff(null);
      setPromoAmountOff(null);

      // Clear parent state
      onPromoCodeIdChange?.(null);
      onPromoDiscountChange?.(null);
      onPromoPercentOffChange?.(null);
      onPromoAmountOffChange?.(null);
    } finally {
      setIsApplyingPromo(false);
    }
  };

  //function to handle the option click
  const handleOptionClick = async (creditsAmount: number, subscription: string, optionKey: string, productId: string) => {
    if (processingOption) return; // Prevent multiple clicks

    console.log("🔍 ManageMembership - handleOptionClick called with productId:", productId);
    setProcessingOption(optionKey);
    try {
      // Allow FREE plan in any environment. Block paid purchases in dev/local.
      if (paymentsDisabledInCurrentEnv && productId !== "league_connect_free") {
        setPaymentDialogSuccess(false);
        setPaymentDialogMessage(DEV_PAYMENT_BLOCK_TOOLTIP);
        setShowPaymentDialog(true);
        return;
      }

      // Handle FREE plan - no payment needed
      if (productId === "league_connect_free") {
        console.log("✅ FREE plan selected - no payment required");

        // Get authentication token
        const accessAuthToken = await getSafeAccessAuth0Token();
        if (!accessAuthToken) {
          console.error("❌ No auth token");
          setPaymentDialogSuccess(false);
          setPaymentDialogMessage("Authentication error. Please log in again.");
          setShowPaymentDialog(true);
          setProcessingOption(null);
          return;
        }

        // Call backend to register FREE plan
        const result = await selectFreePlan(accessAuthToken);

        if (result.success) {
          console.log("✅ FREE plan activated successfully");

          // Check if we're in Capacitor Browser (opened from native app)
          const fromApp = sessionStorage.getItem("fromApp") === "true";
          const isNativePlatform = Capacitor.isNativePlatform();
          const isHybridPlatform = isPlatform("hybrid");
          const isInCapacitorBrowser = fromApp || isNativePlatform || isHybridPlatform;

          if (isInCapacitorBrowser) {
            console.log("📱 FREE plan activated in Capacitor Browser - redirecting to native app");

            // Redirect to native scheme to close browser and notify app (same pattern as Stripe)
            const appId = "com.rotogpt.chat";
            const nativeCallbackUrl = `${appId}://free-plan-activated?success=true`;
            console.log("🔄 Redirecting to native scheme:", nativeCallbackUrl);

            // Try to redirect to native scheme
            try {
              window.location.href = nativeCallbackUrl;
            } catch (e) {
              console.error("Error with href, trying replace:", e);
              try {
                window.location.replace(nativeCallbackUrl);
              } catch (e2) {
                console.error("Error with replace, trying assign:", e2);
                window.location.assign(nativeCallbackUrl);
              }
            }
          } else {
            // Web: show dialog normally
            setPaymentDialogSuccess(true);
            setPaymentDialogMessage("FREE plan activated successfully!");
            setShowPaymentDialog(true);

            // Dispatch freePlanActivated event for web (same pattern as native via App.tsx)
            const freePlanEvent = new CustomEvent("freePlanActivated", {
              detail: {
                success: true,
                message: "FREE plan activated successfully!",
              },
            });
            window.dispatchEvent(freePlanEvent);
            console.log("🔔 Dispatched freePlanActivated event (web)");

            // Close drawer after dialog auto-closes (4 seconds)
            setTimeout(() => {
              if (onOptionSelected) {
                onOptionSelected();
              } else {
                onClose();
              }
            }, 4000);
          }
        } else {
          console.error("❌ Failed to activate FREE plan");
          setPaymentDialogSuccess(false);
          setPaymentDialogMessage("Failed to activate FREE plan. Please try again.");
          setShowPaymentDialog(true);
        }

        setProcessingOption(null);
        return;
      }

      // Validate promo code with selected product if a promo code is applied
      let resolvedPromoCodeId = promoCodeId;
      if (promoCodeId && promoCode.trim()) {
        const accessToken = await getSafeAccessAuth0Token();
        if (!accessToken) {
          setPromoError("Authentication error. Please log in again.");
          setProcessingOption(null);
          return;
        }

        // Re-validate promo code with the selected productId to check minimum amount restrictions
        const validationResult = await applyPromoCode(accessToken, promoCode.trim().toUpperCase(), "stripe", productId);

        if (!validationResult.success) {
          // Promo code doesn't meet requirements for this product (e.g., minimum amount)
          setPromoError(validationResult.message || "This promo code cannot be used with the selected product.");
          setProcessingOption(null);
          return;
        }

        // Update promo code details in case they changed
        if (validationResult.promoCodeId) {
          resolvedPromoCodeId = validationResult.promoCodeId;
          setPromoCodeId(validationResult.promoCodeId);
          setPromoDiscount(validationResult.discountDescription || null);
          setPromoPercentOff(validationResult.percentOff || null);
          setPromoAmountOff(validationResult.amountOff || null);
          onPromoCodeIdChange?.(validationResult.promoCodeId);
          onPromoDiscountChange?.(validationResult.discountDescription || null);
          onPromoPercentOffChange?.(validationResult.percentOff || null);
          onPromoAmountOffChange?.(validationResult.amountOff || null);
        }
      }

      // DIRECT Stripe flow: skip AddPaymentMethod intermediate step and go straight to Checkout
      const accessAuth0Token = await getSafeAccessAuth0Token();
      if (!accessAuth0Token) {
        console.error("❌ No auth token");
        setPaymentDialogSuccess(false);
        setPaymentDialogMessage("Authentication error. Please log in again.");
        setShowPaymentDialog(true);
        return;
      }

      // Native iOS/Android flow: use store purchase directly (no Stripe Checkout redirect)
      if (isIosNative() || isAndroidNative()) {
        // Promo codes are currently validated/applied on Stripe backend flow only.
        if (resolvedPromoCodeId) {
          setPaymentDialogSuccess(false);
          setPaymentDialogMessage("Promo codes are currently not available for native store purchases. Please remove the code and try again.");
          setShowPaymentDialog(true);
          return;
        }

        try {
          const products = await getProducts([productId]);
          const storeName = isIosNative() ? "Apple" : "Google Play";
          if (!products || products.length === 0) {
            setPaymentDialogSuccess(false);
            setPaymentDialogMessage(`This product is not available on ${storeName} at the moment. Please try again later.`);
            setShowPaymentDialog(true);
            return;
          }

          void trackBeginCheckout(analyticsAuthId, productId, planDurationFromProductId(productId), getProductConfig(productId)?.price ?? 0);
          const purchaseResult = await purchaseProduct(productId);

          if (purchaseResult?.status === "pending") {
            setPaymentDialogSuccess(true);
            setPaymentDialogMessage("Purchase is pending confirmation from Apple.");
            setShowPaymentDialog(true);
            return;
          }

          if (purchaseResult?.status === "success") {
            const verifyResult = isIosNative()
              ? await (async () => {
                const jws = (purchaseResult as any).jws as string | undefined;
                const txId = (purchaseResult as any).transactionId as string | number | undefined;
                const receiptPayload = jws && jws.length > 0 ? jws : String(txId ?? "");
                return verifyApplePayment(accessAuth0Token, receiptPayload, productId);
              })()
              : await (async () => {
                const purchaseToken =
                  (purchaseResult as any).purchaseToken ||
                  (purchaseResult as any).transactionId ||
                  "";
                if (!purchaseToken) {
                  return {
                    success: false,
                    message: "Missing Google Play purchase token.",
                  };
                }
                return verifyGooglePayment(accessAuth0Token, String(purchaseToken), productId);
              })();
            if (!verifyResult?.success) {
              setPaymentDialogSuccess(false);
              void trackSubscriptionFailed(analyticsAuthId, productId, "failure", "iap_verify_failed");
              setPaymentDialogMessage(
                verifyResult?.message ||
                (isIosNative() ? "Apple purchase verification failed." : "Google Play purchase verification failed.")
              );
              setShowPaymentDialog(true);
              return;
            }

            if (verifyResult.credits !== undefined) {
              setCredits(verifyResult.credits);
            }
            void trackPurchase(analyticsAuthId, isIosNative() ? "apple_iap" : "google_iap", productId, getProductConfig(productId)?.price ?? 0);

            // When the user tries to subscribe to a plan they're ALREADY subscribed to,
            // native stores can still return a technically successful purchase result and the
            // backend may answer with "already processed/active". Do not show a false success.
            const backendMessage = (verifyResult.message || "").toLowerCase();
            const alreadySubscribed =
              backendMessage.includes("already processed") ||
              backendMessage.includes("already active") ||
              backendMessage.includes("credits not added");

            if (alreadySubscribed) {
              setPaymentDialogSuccess(false);
              setPaymentDialogMessage(
                "You're already subscribed to this plan. No new charge was made."
              );
              setShowPaymentDialog(true);
              return;
            }

            // Native store (Apple Pay / Google Play) already shows success UI — skip redundant dialog
            if (onOptionSelected) {
              onOptionSelected();
            } else {
              onClose();
            }
            return;
          }

          // If the plugin resolved with an unexpected/unknown status we don't treat it
          // as success anymore — previously the outer try/catch fallthrough could make
          // the UI look like the purchase went through. Be explicit here instead.
          setPaymentDialogSuccess(false);
          setPaymentDialogMessage("Purchase was not completed. Please try again.");
          setShowPaymentDialog(true);
          return;
        } catch (error: any) {
          const rawMessage = error?.message || "";
          // Native plugin rejects with "User cancelled" when the user dismisses
          // the store purchase sheet. Show a neutral, accurate message.
          const userCancelled = rawMessage.toLowerCase().includes("user cancel");
          const alreadySubscribedStore =
            rawMessage.toLowerCase().includes("already owned") ||
            rawMessage.toLowerCase().includes("already subscribed");
          setPaymentDialogSuccess(false);
          setPaymentDialogMessage(
            userCancelled
              ? "Purchase was cancelled."
              : alreadySubscribedStore
                ? "You're already subscribed to this plan. No new charge was made."
                : rawMessage ||
                (isIosNative()
                  ? "Apple purchase failed. Please try again."
                  : "Google Play purchase failed. Please try again.")
          );
          setShowPaymentDialog(true);
          return;
        }
      }

      const urlParams = new URLSearchParams(window.location.search);
      const fromAppUrl = urlParams.get("fromApp") === "true";
      const isNativePlatform = Capacitor.isNativePlatform();
      const isHybridPlatform = isPlatform("hybrid");
      const fromAppSession = sessionStorage.getItem("fromApp") === "true";

      if (fromAppUrl && !fromAppSession) {
        sessionStorage.setItem("fromApp", "true");
      }

      const fromApp = fromAppUrl || fromAppSession;
      const isCapacitorContext = fromApp || isNativePlatform || isHybridPlatform;

      // In Capacitor: don't create Checkout Session, just return
      // if (isCapacitorContext) {
      //   return
      // }

      const baseUrl = window.location.origin;
      const sourceParam = isCapacitorContext ? "&source=app" : "";

      const successUrl = isCapacitorContext
        ? `${baseUrl}/stripe-return?payment=success&productId=${productId}${sourceParam}`
        : `${baseUrl}/home?payment=success&productId=${productId}`;
      const cancelUrl = isCapacitorContext
        ? `${baseUrl}/stripe-return?payment=cancelled&productId=${productId}${sourceParam}`
        : `${baseUrl}/home?payment=cancelled&productId=${productId}`;

      void trackBeginCheckout(analyticsAuthId, productId, planDurationFromProductId(productId), getProductConfig(productId)?.price ?? 0);
      const checkoutResult = await createCheckoutSession(accessAuth0Token, productId, successUrl, cancelUrl, resolvedPromoCodeId);

      if (checkoutResult.success && checkoutResult.url) {
        console.log("✅ Checkout Session created, redirecting directly to Stripe:", checkoutResult.url);
        window.location.href = checkoutResult.url;
      } else {
        console.error("❌ Failed to create Checkout Session:", checkoutResult.message);
        void trackSubscriptionFailed(analyticsAuthId, productId, "failure", "checkout_session_failed");
        setPaymentDialogSuccess(false);
        setPaymentDialogMessage(checkoutResult.message || "Failed to create payment session. Please try again.");
        setShowPaymentDialog(true);
      }

      // PREVIOUS FLOW (commented on purpose):
      // Open AddPaymentMethod and then user clicks "Pay with Credit Card" there.
      // if (onOpenAddPaymentMethod) {
      //   console.log(
      //     "🔍 ManageMembership - Calling onOpenAddPaymentMethod with productId:",
      //     productId,
      //     "promoCodeId:",
      //     promoCodeId,
      //     "percentOff:",
      //     promoPercentOff,
      //     "amountOff:",
      //     promoAmountOff
      //   );
      //   onOpenAddPaymentMethod(productId, promoCodeId, promoPercentOff, promoAmountOff);
      // } else {
      //   console.error("❌ ManageMembership - onOpenAddPaymentMethod is not defined!");
      // }
    } catch (error) {
      console.error("Error adding credits:", error);
      setPromoError("An error occurred. Please try again.");
      // Don't close on error, let user try again
    } finally {
      setProcessingOption(null);
    }
  };
  const isBoosterSubscription = userSubscription === "booster_starter" || userSubscription === "booster_power";
  // nice text for the ui
  const userSubscriptionText =
    userSubscription === "annual_pro"
      ? "annual pro"
      : userSubscription === "season_pass"
        ? "season pass"
        : userSubscription === "basic_monthly"
          ? "basic monthly"
          : isBoosterSubscription
            ? "booster pack"
            : userSubscription;

  // Get chatbot and agent endpoint credits from environment variables in dashboard first
  const chatBotCredits = frontEnvNumber("REACT_APP_CHATBOT_CREDITS", 3);
  const agentEndpointCredits = frontEnvNumber("REACT_APP_AGENT_ENDPOINT_CREDITS", 10);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        p={1}
        pb={1}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#EDEDED",
        }}
      >
        <IconButton
          aria-label="back"
          onClick={onBack}
          sx={{
            visibility: isRequired ? "hidden" : "visible",
          }}
        >
          <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
        </IconButton>

        {/* <IconButton aria-label="back" onClick={onBack} disabled={isRequired}>
          <ArrowBackIcon sx={{ color: isRequired ? "#AAAAAA" : `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
        </IconButton> */}
        <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign={"center"}>
          Upgrade
        </Typography>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
          {/* <IconButton aria-label="close" onClick={onClose} disabled={isRequired}> */}
          <IconButton aria-label="close" onClick={onClose}>
            {/* <CloseIcon sx={{ color: isRequired ? "#AAAAAA" : `${PureLightThemeCaaS.colors.alpha.black[100]}` }} /> */}
            <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
          </IconButton>
        </Box>
      </Box>

      {/* Divider for separation*/}
      <Box
        sx={{
          position: "sticky",
          top: "56px",
          zIndex: 10,
          bgcolor: "#EDEDED",
        }}
      >
        <Divider
          sx={{
            margin: {
              xs: "auto",
              md: "0px",
            },
            width: "100%",
            bgcolor: "#000000",
            my: 0.5,
          }}
        />
      </Box>

      {/* Scrollable container - no maxWidth here */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* Content container with maxWidth for normal content */}
        <Box
          sx={{
            pt: 2,
            px: 2,
            "@media (min-width: 1025px)": {
              maxWidth: "60%",
              margin: "0 auto",
            },
          }}
        >
          {/* Promo Code Section */}
          {/* 
        <Box sx={{ mb: 0 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <Box sx={{ flex: 1, position: "relative", minWidth: 0 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter promo code"
                value={promoCode}
                inputProps={{
                  style: {
                    fontSize: "14px",
                    fontWeight: "bold",
                  },
                }}
                onChange={(e) => {
                  const newValue = e.target.value.toUpperCase();
                  setPromoCode(newValue);
                  onPromoCodeChange?.(newValue);
                  setPromoError(null);
                  if (promoCodeId) {
                    // Clear promo code data when user edits the code
                    setPromoCodeId(null);
                    setPromoDiscount(null);
                    setPromoPercentOff(null);
                    setPromoAmountOff(null);
                    onPromoCodeIdChange?.(null);
                    onPromoDiscountChange?.(null);
                    onPromoPercentOffChange?.(null);
                    onPromoAmountOffChange?.(null);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !isApplyingPromo) {
                    handleApplyPromoCode();
                  }
                }}
                disabled={isApplyingPromo}
                error={!!promoError}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
                    height: "30px",
                    "& fieldset": {
                      borderColor: promoError ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.info.lighter,
                      // borderWidth: "1px",
                    },
                    // "&:hover fieldset": {
                    //   borderColor: promoError ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.info.lighter,
                    // },
                    // "&.Mui-focused fieldset": {
                    //   borderColor: promoError ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.info.lighter,
                    // },
                  },
                }}
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handleApplyPromoCode}
              disabled={isApplyingPromo || !promoCode.trim()}
              sx={{
                borderColor: PureLightThemeCaaS.colors.alpha.black[30],
                borderWidth: "0.5px",
                color: PureLightThemeCaaS.colors.alpha.black[100],
                textTransform: "none",
                width: "80px",
                height: "30px",
                "&:hover": {
                  borderColor: PureLightThemeCaaS.colors.alpha.black[100],
                  // bgcolor: "rgba(0, 0, 0, 0.05)",
                },
                "&.Mui-disabled": {
                  borderColor: PureLightThemeCaaS.colors.alpha.gray[30],
                  color: PureLightThemeCaaS.colors.alpha.gray[50],
                },
              }}
            >
              {isApplyingPromo ? <CircularProgress size={20} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> : "Apply"}
            </Button>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", minHeight: "18px", my: 0.5 }}>
            {(promoError || promoDiscount) && (
              <Typography
                variant="body2"
                sx={{
                  ml: 0.2,
                  color: promoError ? "#FF1943" : `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                  fontSize: "12px",
                  fontWeight: "bold",
                  whiteSpace: "normal",
                }}
              >
                {promoError || (promoDiscount ? `✅ ${promoDiscount}` : "")}
              </Typography>
            )}
          </Box>
        </Box>
        */}

          {/* Divider for separation*/}
          {/* <Divider
          sx={{
            margin: {
              xs: "auto",
              md: "0px",
            },
            width: "100%",
            bgcolor: "#E0E0E0",
            // my: 0.5,
          }}
        /> */}

          {/* Headlines */}
          <Box sx={{ pt: 1, pb: 1, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "32px", md: "40px" },
                fontWeight: "bold",
                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                lineHeight: 1.2,
                mb: 0,
                textTransform: "uppercase",
                fontStyle: "italic",
              }}
            >
              STOP GUESSING.
            </Typography>
            <Typography
              variant="h3"
              component="span"
              sx={{
                fontSize: { xs: "32px", md: "40px" },
                fontWeight: "bold",
                background: `linear-gradient(to right, #f97316 0%, #dc2626 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                lineHeight: 1.2,
                mb: 0.5,
                textTransform: "uppercase",
                fontStyle: "italic",
              }}
            >
              START DOMINATING.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "14px", md: "16px" },
                color: `${PureLightThemeCaaS.colors.alpha.black[50]}`,
                lineHeight: 1.2,
                maxWidth: "500px",
                margin: "0 auto",
                fontWeight: "bold",
              }}
            >
              {/* Don't let luck decide your season. Arm yourself with the AI that 100x your fantasy intelligence. */}
              Chatbot costs {chatBotCredits} credits per question. Analysis functions like Start/Sit, Waivers and Trade costs {agentEndpointCredits} credits per request.
            </Typography>
          </Box>

          {/* {userSubscription && !isBoosterSubscription && (
            <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" sx={{ p: 1 }}>
              <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} sx={{ fontWeight: "bolder", fontSize: "15px" }}>
                Your current subscription is {userSubscriptionText}
              </Typography>
            </Box>
          )} */}
          {/* Tabs for Monthly Plans and Booster Packs */}
          <Box sx={{ pt: 0 }}>
            <Box
              sx={{
                display: "flex",
                bgcolor: "#E0E0E0",
                borderRadius: 0.5,
                p: 0.5,
                gap: 0.5,
                height: "40px",
              }}
            >
              <Button
                onClick={() => setActiveTab(0)}
                sx={{
                  flex: 1,
                  borderRadius: 0.5,
                  textTransform: "none",
                  color: activeTab === 0 ? `${PureLightThemeCaaS.colors.alpha.black[100]}` : `${PureLightThemeCaaS.colors.alpha.black[70]}`,
                  bgcolor: activeTab === 0 ? "#FFFFFF" : "transparent",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: activeTab === 0 ? "#FFFFFF" : "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Membership
              </Button>
              <Button
                onClick={() => setActiveTab(1)}
                sx={{
                  flex: 1,
                  borderRadius: 0.5,
                  textTransform: "none",
                  color: activeTab === 1 ? `${PureLightThemeCaaS.colors.alpha.black[100]}` : `${PureLightThemeCaaS.colors.alpha.black[70]}`,
                  bgcolor: activeTab === 1 ? "#FFFFFF" : "transparent",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: activeTab === 1 ? "#FFFFFF" : "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Booster Packs
              </Button>
            </Box>
          </Box>

          {/* Monthly Plans Content */}
          {activeTab === 0 && (
            <SubsPlans
              monthlyPlans={getMonthlyPlans(seasonPassPrice || "1")}
              handleOptionClick={handleOptionClick}
              hoveredMonthlyPlan={hoveredMonthlyPlan}
              setHoveredMonthlyPlan={setHoveredMonthlyPlan}
              processingOption={processingOption}
              userSubscription={userSubscription}
            />
          )}

          {/* Booster Packs Content */}
          {activeTab === 1 && (
            <BoosterPacks
              boosterPacks={buildBoosterPacks()}
              handleOptionClick={handleOptionClick}
              hoveredBoosterPack={hoveredBoosterPack}
              setHoveredBoosterPack={setHoveredBoosterPack}
              processingOption={processingOption}
            />
          )}

          <Box sx={{ pt: 2, pb: 2, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "32px", md: "40px" },
                fontWeight: "bold",
                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                lineHeight: 1.2,
                mb: 0,
                textTransform: "uppercase",
                fontStyle: "italic",
              }}
            >
              The Winning Playbook
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "14px", md: "16px" },
                color: `${PureLightThemeCaaS.colors.alpha.black[50]}`,
                lineHeight: 1.2,
                maxWidth: "500px",
                margin: "0 auto",
                fontWeight: "bold",
              }}
            >
              Clear lanes for every level of the game.
            </Typography>
          </Box>
        </Box>

        {/* Pricing Table - Outside maxWidth container to allow full width */}
        <Box
          sx={{
            width: "100%",
            pb: 3,
            pr: 2,
            pl: 2,
          }}
        >
          <TablePricing />
        </Box>

        {/* Disclaimer */}
        {/* <Box sx={{ mt: 2, mb: 3, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: "#000000",
            }}
          >
            Subscriptions auto-renew. Rollover credits from the previous month are valid for an additional 30 days.
          </Typography>
        </Box> */}
        <Box sx={{ mt: 1, mb: 3, textAlign: "center", px: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: `${PureLightThemeCaaS.colors.alpha.black[70]}`,
              lineHeight: 1.5,
            }}
          >
            By subscribing, you agree to our{" "}
            <Link
              href={getTermsConditionsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              onClick={async (e) => {
                if (Capacitor.isNativePlatform()) {
                  e.preventDefault();
                  await Browser.open({ url: getTermsConditionsUrl() });
                }
              }}
              sx={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Terms and Conditions
            </Link>
            {" "}and{" "}
            <Link
              href={getPrivacyPolicyUrl()}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              onClick={async (e) => {
                if (Capacitor.isNativePlatform()) {
                  e.preventDefault();
                  await Browser.open({ url: getPrivacyPolicyUrl() });
                }
              }}
              sx={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Privacy Policy
            </Link>
            .
          </Typography>
        </Box>
      </Box>

      {/* Payment Dialog */}
      <PaymentDialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} success={paymentDialogSuccess} message={paymentDialogMessage} />
    </Box>
  );
};
